/**
 * Palettes (doc 10 §Palettes, binding hex values) and the derived ramps every
 * entity is allowed to use (Law L5: <= 12 unique colours per entity, all drawn
 * from the active depth palette plus the entity's signature-light ramp).
 *
 * Ramp factors are constants, not taste: two entities asking for the same face
 * of the same base colour get the exact same pixel, so `paletteCompliance` can
 * be a set-membership test rather than a judgement call.
 */

import { hex, mix, shade, type Color } from './surface.ts';

export type DepthId = 'd1' | 'd2' | 'd3';

export interface DepthPalette {
  id: DepthId;
  name: string;
  waterTop: Color;
  waterBottom: Color;
  terrain: Color;
  structureBase: Color;
  structureEmissive: Color;
  playerGlow: Color;
  enemyGlow: Color;
  uiInk: Color;
}

export const DEPTHS: Record<DepthId, DepthPalette> = {
  d1: {
    id: 'd1',
    name: 'THE SHELF',
    waterTop: hex('#0b3a4a'),
    waterBottom: hex('#062530'),
    terrain: hex('#123c46'),
    structureBase: hex('#2a4d57'),
    structureEmissive: hex('#ffd45e'),
    playerGlow: hex('#5ff2c8'),
    enemyGlow: hex('#b7ff5e'),
    uiInk: hex('#e8f4f2'),
  },
  d2: {
    id: 'd2',
    name: 'THE GLOOM',
    waterTop: hex('#0a1e3d'),
    waterBottom: hex('#040d20'),
    terrain: hex('#0e1b33'),
    structureBase: hex('#2a4d57'),
    structureEmissive: hex('#ffc14d'),
    playerGlow: hex('#59c9ff'),
    enemyGlow: hex('#c86bff'),
    uiInk: hex('#e8f4f2'),
  },
  d3: {
    id: 'd3',
    name: 'THE FLOOR',
    waterTop: hex('#050510'),
    waterBottom: hex('#020208'),
    terrain: hex('#0d0a14'),
    structureBase: hex('#2a4d57'),
    structureEmissive: hex('#ff9e3d'),
    playerGlow: hex('#ffb35c'),
    enemyGlow: hex('#ff4d6a'),
    uiInk: hex('#e8f4f2'),
  },
};

export const DEPTH_IDS: DepthId[] = ['d1', 'd2', 'd3'];

/** Law L6: the one contact-outline colour. Never per-sprite, never varied. */
export const OUTLINE = hex('#020709');

/** Doc 10: hover recolours the contact outline instead of adding geometry. */
export const OUTLINE_HOVER = hex('#5ff2c8');

/** Doc 17 §8 — semantic colours, kept out of every building's signature ramp. */
export const SEMANTIC = {
  good: hex('#5ff2c8'),
  warning: hex('#ffd45e'),
  critical: hex('#ff4d6a'),
  ink: hex('#e8f4f2'),
  inkDim: hex('#8fa8ad'),
} as const;

/**
 * The five faces of an extruded iso volume, lit from the upper left.
 * Order matters for readability: top is brightest, the SE face darkest, so a
 * cube reads as a cube with no outline at all.
 */
export interface VolumeRamp {
  top: Color;
  left: Color;
  right: Color;
  edge: Color;
  deep: Color;
}

/**
 * Ramp factors are deliberately bright. Doc 10 gives structures the base
 * `#2a4d57`, which sits almost exactly on the value of D1's rock — stage 0's
 * `silhouetteContrast` check caught it: at these depths a building drawn near
 * the terrain's own luminance dissolves into it, and no amount of outline
 * rescues that. Structures are lit metal, rock is not, so the ramp opens the
 * gap where it belongs.
 */
export function volumeRamp(base: Color): VolumeRamp {
  return {
    top: shade(base, 1.62),
    left: shade(base, 1.24),
    right: shade(base, 0.86),
    edge: shade(base, 2.0),
    deep: shade(base, 0.52),
  };
}

/** Rubble left by a destroyed structure (doc 03: ruins hold the footprint). */
export function ruinRamp(base: Color): VolumeRamp {
  return {
    top: shade(base, 0.62),
    left: shade(base, 0.52),
    right: shade(base, 0.42),
    edge: shade(base, 0.78),
    deep: shade(base, 0.31),
  };
}

/** A signature light: off/idle, on, hot (firing or full), plus its halo. */
export interface SignatureRamp {
  dim: Color;
  on: Color;
  hot: Color;
  halo: Color;
}

export function signatureRamp(sig: Color): SignatureRamp {
  return {
    dim: shade(sig, 0.42),
    on: sig,
    hot: mix(sig, hex('#ffffff'), 0.45),
    halo: shade(sig, 0.65),
  };
}

/**
 * The fauna body ramp (doc 16 §0, creature lane).
 *
 * Fauna gets its OWN base rather than the structures' `#2a4d57`, for the same
 * reason structures got a brighter ramp than doc 10's raw hex in stage 0: the
 * placeholder fauna was drawn at `#20343a` and measured a mean luminance of
 * 0.177 against a D1 seabed at 0.152 — a delta of 0.052 where `silhouetteContrast`
 * demands 0.18. Every Shelf and Gloom species was dissolving into the floor and
 * no test could see it, because fauna sat outside the registry.
 *
 * `#6a7186` is a cold violet-grey: it clears the floor with a 0.085 margin at
 * every depth, and it is deliberately NOT the structures' cyan-steel, so doc
 * 10's "player = warm and rectilinear, ocean = cold and curved" split survives
 * the fact that both are now bright enough to see.
 *
 * One base at every depth, not three. The species table is shared — a
 * Broodmother at THE GLOOM spawns twelve Shelf krill — and the same animal
 * cannot be two colours inside one fight. Depth speaks through the water, the
 * terrain and the bioluminescence.
 */
export const FAUNA_BASE = hex('#6a7186');

export interface FaunaRamp {
  /** Lit dorsal surface: the top half of a body. */
  back: Color;
  /** Mid-tone flank, and anything turned toward the light. */
  flank: Color;
  /** Shaded belly: the bottom half. */
  belly: Color;
  /** Fins, tails, legs, antennae — one step under the belly. */
  fin: Color;
  /** Plate bands, teeth and bone: the brightest structural colour on an animal. */
  bone: Color;
  /** Mouth interiors and sockets — the one near-black that is not the outline. */
  maw: Color;
}

/**
 * Ramp factors, spread wide on purpose. The first draft used 1.18/0.74 for back
 * and belly — a 1.6x range where the structure ramp spans 3.8x — and every
 * animal in the bestiary sheet came out a flat grey cut-out with a light on it.
 * A body only reads as a volume if its lit half and its shaded half are as far
 * apart as a building's top face and its deep corner.
 */
export function faunaRamp(): FaunaRamp {
  return {
    back: shade(FAUNA_BASE, 1.32),
    flank: FAUNA_BASE,
    belly: shade(FAUNA_BASE, 0.64),
    fin: shade(FAUNA_BASE, 0.48),
    bone: shade(FAUNA_BASE, 1.58),
    maw: shade(FAUNA_BASE, 0.2),
  };
}

/**
 * The colours a creature may legally emit (L5 membership test, creature lane).
 * Six body faces + the depth's bioluminescence ramp + the outline + the ink the
 * hurt flash paints with: eleven, under Law L5's twelve.
 */
export function allowedFauna(depth: DepthPalette): Set<Color> {
  const f = faunaRamp();
  const g = signatureRamp(depth.enemyGlow);
  return new Set<Color>([
    OUTLINE,
    f.back,
    f.flank,
    f.belly,
    f.fin,
    f.bone,
    f.maw,
    g.dim,
    g.on,
    g.hot,
    g.halo,
    depth.uiInk,
  ]);
}

/** Ground checkerboard and the foundation pad a structure stands on (doc 10 rule 2). */
export interface GroundRamp {
  tileA: Color;
  tileB: Color;
  padFill: Color;
  padRim: Color;
  contactShadow: Color;
}

/**
 * The floor is darker than doc 10's raw terrain hex, for the same reason the
 * volume ramp is brighter: 4000 m of water over a rock does not read as a lit
 * surface. Together the two changes buy the value separation that
 * `silhouetteContrast` demands, and they push the scene toward the "cathedral
 * at night" brief rather than away from it.
 */
export function groundRamp(p: DepthPalette): GroundRamp {
  return {
    tileA: shade(p.terrain, 0.76),
    tileB: shade(p.terrain, 0.56),
    padFill: shade(p.terrain, 0.34),
    padRim: shade(p.terrain, 0.95),
    contactShadow: hex('#02070980'),
  };
}

/**
 * The colours an entity may legally emit at a given depth (L5 membership test).
 * Anything outside this set fails `paletteCompliance`.
 */
export function allowedPalette(depth: DepthPalette, signature: Color): Set<Color> {
  const v = volumeRamp(depth.structureBase);
  const ruin = ruinRamp(depth.structureBase);
  const s = signatureRamp(signature);
  const g = groundRamp(depth);
  return new Set<Color>([
    OUTLINE,
    OUTLINE_HOVER,
    v.top,
    v.left,
    v.right,
    v.edge,
    v.deep,
    ruin.top,
    ruin.left,
    ruin.right,
    ruin.edge,
    ruin.deep,
    s.dim,
    s.on,
    s.hot,
    s.halo,
    depth.structureEmissive,
    depth.playerGlow,
    depth.uiInk,
    g.padFill,
    g.padRim,
  ]);
}
